import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateApplication, useUpdateApplication } from "@/api/devices";
import type { DeviceApplication } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  application?: DeviceApplication | null;
}

export function ApplicationModal({ open, onClose, tenantId, deviceId, application }: Props) {
  const isEdit = !!application;
  const create = useCreateApplication(tenantId, deviceId);
  const update = useUpdateApplication(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [name, setName] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [lastOpenedAt, setLastOpenedAt] = useState("");

  useEffect(() => {
    if (open) {
      setName(application?.name ?? "");
      setBundleId(application?.bundle_id ?? "");
      setLastOpenedAt(application?.last_opened_at ? application.last_opened_at.slice(0, 16) : "");
    }
  }, [open, application]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      bundle_id: bundleId,
      last_opened_at: lastOpenedAt ? new Date(lastOpenedAt).toISOString() : null,
    };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: application.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Application</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Name *</label>
          <Input value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Bundle ID</label>
          <Input value={bundleId} onChange={e => setBundleId(e.target.value)} placeholder="com.example.app" />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Last Opened</label>
          <Input type="datetime-local" value={lastOpenedAt} onChange={e => setLastOpenedAt(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending || !name}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
